import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { state } from './state.js';
import { STYLES_DATA, MODULAR_HOUSES_CONFIG, BASE_Z } from './styles-data.js';
import { loadCachedTexture, createContactShadowTexture, createAsphaltTexture } from './textures.js';
import { setupTeaStallProps, createSeamCoverPole } from './props.js';
import { getActiveCamera } from './camera-controls.js';

/**
 * Khởi tạo dãy 4 căn nhà modular xếp so le trục Z
 */
function setupModularTownMeshes() {
  const defaultHouses = MODULAR_HOUSES_CONFIG.ghibli;
  const contactShadowTex = createContactShadowTexture();
  const contactShadowMat = new THREE.MeshBasicMaterial({
    map: contactShadowTex,
    transparent: true,
    opacity: 0.85,
    depthWrite: false
  });

  defaultHouses.forEach((cfg, idx) => {
    const geo = new THREE.PlaneGeometry(cfg.w, cfg.h);
    const mat = new THREE.MeshStandardMaterial({
      transparent: true,
      alphaTest: 0.1,
      depthWrite: true,
      roughness: 0.82,
      side: THREE.DoubleSide
    });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.name = `ModularHouse_${idx + 1}`;
    mesh.position.set(cfg.x, cfg.h / 2, cfg.baseZ);
    mesh.castShadow = true;
    mesh.receiveShadow = false;
    state.modularTownGroup.add(mesh);

    const shadowGeo = new THREE.PlaneGeometry(cfg.w * 1.06, 0.9);
    const shadowMesh = new THREE.Mesh(shadowGeo, contactShadowMat);
    shadowMesh.rotation.x = -Math.PI / 2;
    shadowMesh.position.set(cfg.x, 0.008, cfg.baseZ + 0.45);
    state.contactShadowGroup.add(shadowMesh);

    state.modularHouseMeshes.push({
      mesh,
      shadowMesh,
      baseX: cfg.x,
      baseZ: cfg.baseZ,
      w: cfg.w,
      h: cfg.h
    });
  });
}

/**
 * Khởi tạo Scene, Cameras, Renderer, Lights & 2.5D Layers
 */
export function initScene(canvas) {
  state.canvas = canvas;
  state.scene = new THREE.Scene();

  // 1. Cameras
  state.perspCamera = new THREE.PerspectiveCamera(45, window.innerWidth / window.innerHeight, 0.1, 200);
  state.perspCamera.position.set(0, 2.8, 16.0);

  state.frustumHeight = 11.2;
  state.frameShiftY = 1.6;
  const aspect = window.innerWidth / window.innerHeight;
  const frustumWidth = state.frustumHeight * aspect;
  state.orthoCamera = new THREE.OrthographicCamera(
    -frustumWidth / 2,
    frustumWidth / 2,
    state.frustumHeight / 2 + state.frameShiftY,
    -state.frustumHeight / 2 + state.frameShiftY,
    0.1,
    200
  );
  state.orthoCamera.position.set(0, 2.8, 16.0);

  // 2. Renderer
  state.renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: true,
    powerPreference: 'high-performance'
  });
  state.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  state.renderer.setSize(window.innerWidth, window.innerHeight);
  state.renderer.shadowMap.enabled = true;
  state.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  state.renderer.toneMapping = THREE.ACESFilmicToneMapping;
  state.renderer.toneMappingExposure = 1.06;
  state.renderer.outputColorSpace = THREE.SRGBColorSpace;

  // 3. OrbitControls
  state.controls = new OrbitControls(getActiveCamera(), state.renderer.domElement);
  state.controls.enableDamping = true;
  state.controls.dampingFactor = 0.08;
  state.controls.target.set(0, 3.8, 0);
  state.controls.maxPolarAngle = Math.PI / 2 + 0.12;
  state.controls.minDistance = 4;
  state.controls.maxDistance = 55;
  state.controls.enabled = false;

  // 4. Lights
  state.ambientLight = new THREE.AmbientLight(0xe0f2fe, 0.8);
  state.scene.add(state.ambientLight);

  state.dirLight = new THREE.DirectionalLight(0xfffaed, 1.6);
  state.dirLight.position.set(10, 18, 12);
  state.dirLight.castShadow = true;
  state.dirLight.shadow.mapSize.width = 2048;
  state.dirLight.shadow.mapSize.height = 2048;
  state.dirLight.shadow.camera.near = 0.5;
  state.dirLight.shadow.camera.far = 45;
  state.dirLight.shadow.camera.left = -18;
  state.dirLight.shadow.camera.right = 18;
  state.dirLight.shadow.camera.top = 18;
  state.dirLight.shadow.camera.bottom = -4;
  state.dirLight.shadow.bias = -0.0004;
  state.scene.add(state.dirLight);

  state.cyanPointLight = new THREE.PointLight(0x00f0ff, 0, 18, 1.2);
  state.cyanPointLight.position.set(-3.2, 2.4, 2.0);
  state.scene.add(state.cyanPointLight);

  state.magentaPointLight = new THREE.PointLight(0xff007f, 0, 18, 1.2);
  state.magentaPointLight.position.set(3.2, 2.8, 1.5);
  state.scene.add(state.magentaPointLight);

  state.lanternPointLight = new THREE.PointLight(0xffba59, 1.2, 10, 1.6);
  state.lanternPointLight.position.set(-8.2, 7.2, 6.8);
  state.scene.add(state.lanternPointLight);

  // 5. 2.5D Layers
  // Hậu cảnh bầu trời
  const bgGeo = new THREE.PlaneGeometry(80, 44);
  state.bgMat = new THREE.MeshBasicMaterial({
    color: 0xffffff,
    depthWrite: false,
    side: THREE.DoubleSide
  });
  state.bgMesh = new THREE.Mesh(bgGeo, state.bgMat);
  state.bgMesh.name = 'Layer_Background';
  state.bgMesh.position.set(0, 14.5, BASE_Z.bg);
  state.scene.add(state.bgMesh);

  // Tiền cảnh cột đèn
  const fgHeight = 11.8;
  const fgWidth = fgHeight * (1376 / 768);
  const fgGeo = new THREE.PlaneGeometry(fgWidth, fgHeight);
  state.fgMat = new THREE.MeshStandardMaterial({
    transparent: true,
    alphaTest: 0.05,
    depthWrite: false,
    roughness: 0.85,
    side: THREE.DoubleSide
  });
  state.fgMesh = new THREE.Mesh(fgGeo, state.fgMat);
  state.fgMesh.name = 'Layer_Foreground_CleanLamp';
  state.fgMesh.position.set(-3.5, fgHeight / 2, BASE_Z.fg);
  state.scene.add(state.fgMesh);

  loadCachedTexture('/assets/2.5d-layers/clean_lamp_fg.png', false).then((tex) => {
    state.fgMat.map = tex;
    state.fgMat.needsUpdate = true;
  });

  // Dãy nhà modular
  state.modularTownGroup = new THREE.Group();
  state.modularTownGroup.name = 'Modular_Town_Group';
  state.scene.add(state.modularTownGroup);

  state.contactShadowGroup = new THREE.Group();
  state.contactShadowGroup.name = 'Contact_Shadow_Group';
  state.scene.add(state.contactShadowGroup);

  setupModularTownMeshes();

  // Sàn vỉa hè 3D
  state.layerCenterGroup = new THREE.Group();
  state.layerCenterGroup.name = 'Layer_Center_3D';
  state.scene.add(state.layerCenterGroup);

  state.groundGroup = new THREE.Group();
  state.groundGroup.name = 'Ground_Pavement';

  state.pavementTextures.asphalt = createAsphaltTexture();

  state.sidewalkMat = new THREE.MeshStandardMaterial({
    roughness: 0.8,
    metalness: 0.05
  });

  const undergroundGeo = new THREE.PlaneGeometry(90, 32);
  const undergroundMat = new THREE.MeshBasicMaterial({ color: 0x111622 });
  const undergroundMesh = new THREE.Mesh(undergroundGeo, undergroundMat);
  undergroundMesh.position.set(0, -16.0, -9.8);
  state.groundGroup.add(undergroundMesh);

  const sidewalkGeo = new THREE.PlaneGeometry(60, 21);
  state.sidewalkMesh = new THREE.Mesh(sidewalkGeo, state.sidewalkMat);
  state.sidewalkMesh.rotation.x = -Math.PI / 2;
  state.sidewalkMesh.position.set(0, 0.0, 1.0);
  state.sidewalkMesh.receiveShadow = true;
  state.groundGroup.add(state.sidewalkMesh);

  const curbMat = new THREE.MeshStandardMaterial({ color: 0x64748b, roughness: 0.6 });
  const curbGeo = new THREE.BoxGeometry(60, 0.28, 0.35);
  state.curbMesh = new THREE.Mesh(curbGeo, curbMat);
  state.curbMesh.position.set(0, -0.07, 11.5);
  state.curbMesh.receiveShadow = true;
  state.groundGroup.add(state.curbMesh);

  const roadMat = new THREE.MeshStandardMaterial({
    color: 0x1e2633,
    roughness: 0.9,
    metalness: 0.05
  });
  const roadGeo = new THREE.PlaneGeometry(60, 10);
  state.roadMesh = new THREE.Mesh(roadGeo, roadMat);
  state.roadMesh.rotation.x = -Math.PI / 2;
  state.roadMesh.position.set(0, -0.14, 16.5);
  state.roadMesh.receiveShadow = true;
  state.groundGroup.add(state.roadMesh);

  state.layerCenterGroup.add(state.groundGroup);

  // Đạo cụ quán trà đá
  state.propsGroup = new THREE.Group();
  state.propsGroup.name = 'Sidewalk_Props_3D';
  setupTeaStallProps(state.propsGroup);
  state.layerCenterGroup.add(state.propsGroup);

  // Đạo cụ cột điện che đường nối module nhà tại x=0.18, Z=-8.5
  state.seamCoverPole = createSeamCoverPole();
  state.seamCoverPole.position.set(0.18, 0, -8.5);
  state.scene.add(state.seamCoverPole);
}

/**
 * Áp dụng loại mặt sàn vỉa hè
 */
export async function applyPavement(type) {
  state.currentPavement = type;

  if (type === 'terrazzo' && !state.pavementTextures.terrazzo) {
    const tex = await loadCachedTexture('/assets/2.5d-layers/tile_terrazzo.jpg', false);
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.RepeatWrapping;
    tex.repeat.set(state.pavementRepeat, Math.round(state.pavementRepeat * 0.45));
    state.pavementTextures.terrazzo = tex;
  } else if (type === 'stone' && !state.pavementTextures.stone) {
    const tex = await loadCachedTexture('/assets/2.5d-layers/tile_stone.jpg', false);
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.RepeatWrapping;
    tex.repeat.set(state.pavementRepeat * 1.3, Math.round(state.pavementRepeat * 0.6));
    state.pavementTextures.stone = tex;
  }

  const activeTex = state.pavementTextures[type];
  if (activeTex && state.sidewalkMat) {
    state.sidewalkMat.map = activeTex;
    state.sidewalkMat.needsUpdate = true;
  }

  document.querySelectorAll('.pavement-btn').forEach((btn) => {
    btn.classList.toggle('active', btn.getAttribute('data-pavement') === type);
  });

  const badge = document.getElementById('pavement-badge');
  if (badge) badge.textContent = type.toUpperCase();

  const hudStyle = document.getElementById('hud-active-style');
  if (hudStyle && STYLES_DATA[state.currentStyleKey]) {
    const styleName = STYLES_DATA[state.currentStyleKey].name;
    const paveName = type === 'terrazzo' ? 'Terrazzo' : type === 'stone' ? 'Đá Cubic' : 'Nhựa Đường';
    hudStyle.textContent = `${styleName} • ${paveName}`;
  }
}

/**
 * Cập nhật repeat của sàn
 */
export function updatePavementRepeat(repeatVal) {
  state.pavementRepeat = repeatVal;
  Object.keys(state.pavementTextures).forEach((key) => {
    const tex = state.pavementTextures[key];
    if (tex) {
      const ratio = key === 'stone' ? 1.3 : 1.0;
      tex.repeat.set(repeatVal * ratio, Math.round(repeatVal * 0.5));
      tex.needsUpdate = true;
    }
  });
  const tag = document.getElementById('val-pave-repeat');
  if (tag) tag.textContent = `${repeatVal}x`;
}

/**
 * Đồng bộ Phong cách và Dãy nhà modular
 */
export async function applyStyle(styleKey) {
  const style = STYLES_DATA[styleKey];
  if (!style) return;

  state.currentStyleKey = styleKey;
  const loadingToast = document.getElementById('loading-toast');
  const loadingMsg = document.getElementById('loading-msg');
  if (loadingToast && loadingMsg) {
    loadingToast.style.display = 'flex';
    loadingMsg.textContent = `Đang đồng bộ phong cách ${style.name}...`;
  }

  try {
    const housesConfig = MODULAR_HOUSES_CONFIG[styleKey] || MODULAR_HOUSES_CONFIG.ghibli;

    const loadPromises = [
      loadCachedTexture(style.bg, style.isPixel),
      ...housesConfig.map((cfg) => loadCachedTexture(cfg.file, style.isPixel))
    ];

    const [bgTex, ...houseTextures] = await Promise.all(loadPromises);

    if (state.bgMat) {
      state.bgMat.map = bgTex;
      state.bgMat.needsUpdate = true;
    }

    houseTextures.forEach((tex, idx) => {
      if (state.modularHouseMeshes[idx]) {
        const hMesh = state.modularHouseMeshes[idx].mesh;
        hMesh.material.map = tex;
        hMesh.material.needsUpdate = true;

        const cfg = housesConfig[idx];
        if (cfg) {
          hMesh.geometry.dispose();
          hMesh.geometry = new THREE.PlaneGeometry(cfg.w, cfg.h);
          hMesh.position.set(cfg.x, cfg.h / 2, cfg.baseZ * state.zSpacingFactor);

          const sMesh = state.modularHouseMeshes[idx].shadowMesh;
          sMesh.geometry.dispose();
          sMesh.geometry = new THREE.PlaneGeometry(cfg.w * 1.06, 0.9);
          sMesh.position.set(cfg.x, 0.008, cfg.baseZ * state.zSpacingFactor + 0.45);

          state.modularHouseMeshes[idx].baseX = cfg.x;
          state.modularHouseMeshes[idx].baseZ = cfg.baseZ;
          state.modularHouseMeshes[idx].w = cfg.w;
          state.modularHouseMeshes[idx].h = cfg.h;
        }
      }
    });

    state.scene.background = new THREE.Color(style.bgColor);
    state.scene.fog = new THREE.FogExp2(style.fogColor, style.fogDensity);

    state.ambientLight.color.setHex(style.ambColor);
    state.ambientLight.intensity = style.ambIntensity;

    state.dirLight.color.setHex(style.dirColor);
    state.dirLight.intensity = style.dirIntensity;

    if (style.cyberNeon) {
      state.cyanPointLight.intensity = 3.5;
      state.magentaPointLight.intensity = 4.0;
    } else {
      state.cyanPointLight.intensity = 0;
      state.magentaPointLight.intensity = 0;
    }

    document.querySelectorAll('.style-btn').forEach((btn) => {
      btn.classList.toggle('active', btn.getAttribute('data-style') === styleKey);
    });

    const hudStyle = document.getElementById('hud-active-style');
    if (hudStyle) {
      const paveName =
        state.currentPavement === 'terrazzo'
          ? 'Terrazzo'
          : state.currentPavement === 'stone'
            ? 'Đá Cubic'
            : 'Nhựa Đường';
      hudStyle.textContent = `${style.name} • ${paveName}`;
    }
  } catch (err) {
    console.error('Lỗi khi đổi style:', err);
  } finally {
    if (loadingToast) {
      setTimeout(() => {
        loadingToast.style.display = 'none';
      }, 180);
    }
  }
}

/**
 * Cập nhật Z-Spacing tương tác đồng bộ
 */
export function updateZSpacing(factor) {
  state.zSpacingFactor = factor;

  if (state.bgMesh) state.bgMesh.position.z = BASE_Z.bg * factor;
  if (state.fgMesh) state.fgMesh.position.z = BASE_Z.fg * factor;
  if (state.lanternPointLight) state.lanternPointLight.position.z = BASE_Z.fg * factor - 0.2;
  if (state.seamCoverPole) state.seamCoverPole.position.z = -8.5 * factor;

  state.modularHouseMeshes.forEach((item) => {
    item.mesh.position.z = item.baseZ * factor;
    item.shadowMesh.position.z = item.baseZ * factor + 0.45;
  });

  const tagBg = document.getElementById('tag-z-bg');
  const tagFg = document.getElementById('tag-z-fg');
  const tagModular = document.getElementById('tag-z-modular');

  if (tagBg) tagBg.textContent = `Z = ${(BASE_Z.bg * factor).toFixed(1)}m`;
  if (tagFg) tagFg.textContent = `Z = +${(BASE_Z.fg * factor).toFixed(1)}m`;
  if (tagModular) tagModular.textContent = `Z ≈ ${(-9.0 * factor).toFixed(1)}m`;

  const zBadge = document.getElementById('z-factor-badge');
  if (zBadge) zBadge.textContent = `${factor.toFixed(2)}x`;
  const valZTag = document.getElementById('val-z-spacing');
  if (valZTag) valZTag.textContent = `${factor.toFixed(2)}x`;
}
